<template>
  <aside class="dep">
    <div v-if="tab !== 'widget' || !selected" class="dep-tabs">
      <button v-for="t in tabs" :key="t" class="ov-dep-tab" :class="{ active: tab === t || (t === 'widgets' && tab === 'widget') }" @click="$emit('update:tab', t)">
        {{ $t(`dash.overview.dashboards.tabs.${t}`) }}
      </button>
    </div>
    <div v-else class="dep-subhead">
      <button class="ov-dep-back" :title="$t('dash.overview.dashboards.back_catalog')" @click="$emit('update:tab', 'widgets'); $emit('deselect')">
        <i class="material-icons">arrow_back</i>
      </button>
      <i class="material-icons dep-subhead-icon">{{ selectedDef.icon }}</i>
      <span class="dep-subhead-title">{{ $t(`dash.overview.widgets.${selected.module}.title`) }}</span>
    </div>

    <!-- Catalogue : glisser sur la grille ou cliquer pour ajouter en bas -->
    <div v-if="tab === 'widgets' || (tab === 'widget' && !selected)" class="dep-body">
      <div class="dep-search">
        <i class="material-icons">search</i>
        <input v-model="search" :placeholder="$t('dash.overview.dashboards.search_widget')" />
      </div>
      <div v-for="cat in catalog" :key="cat.key" class="dep-cat">
        <p class="dep-cat-title">{{ $t(`dash.overview.dashboards.categories.${cat.key}`) }}</p>
        <div class="dep-tiles">
          <Tooltip v-for="w in cat.widgets" :key="w.key" :text="tileHelp(w)" position="left" class="dep-tile-wrap">
            <div
              class="dep-tile"
              :class="{ 'dep-tile--used': usedCount[w.key] }"
              draggable="true"
              @dragstart="onDragStart($event, w)"
              @dragend="$emit('catalog-drag', null)"
              @click="$emit('add', w.key)"
            >
              <i class="material-icons">{{ w.icon }}</i>
              <span>{{ $t(`dash.overview.widgets.${w.key}.title`) }}</span>
              <!-- Déjà présent dans le tableau de bord (ajoutable à nouveau) -->
              <em v-if="usedCount[w.key]" class="dep-tile-used">
                <i class="material-icons">check</i><template v-if="usedCount[w.key] > 1">{{ usedCount[w.key] }}</template>
              </em>
            </div>
          </Tooltip>
        </div>
      </div>
      <p v-if="!catalog.length" class="dep-help">{{ $t('dash.overview.dashboards.no_widget_found') }}</p>
      <p v-else class="dep-hint"><i class="material-icons">touch_app</i>{{ $t('dash.overview.dashboards.catalog_hint') }}</p>
    </div>

    <!-- Réglages du tableau de bord -->
    <div v-else-if="tab === 'settings'" class="dep-body">
      <label class="dep-label">{{ $t('dash.overview.dashboards.name') }}</label>
      <input class="dep-input" :value="draft.name" maxlength="60" @input="set('name', $event.target.value)" @blur="restoreName($event)" />

      <label class="dep-label">{{ $t('dash.overview.dashboards.visibility_label') }}</label>
      <div class="dep-segment">
        <button v-for="v in ['private', 'shared']" :key="v" class="ov-dep-seg" :class="{ active: draft.visibility === v }" @click="set('visibility', v)">
          <i class="material-icons">{{ v === 'shared' ? 'people' : 'lock_outline' }}</i>
          {{ $t(`dash.overview.dashboards.visibility.${v}`) }}
        </button>
      </div>
      <p class="dep-help dep-help--tight">{{ $t(`dash.overview.dashboards.visibility_help.${draft.visibility || 'private'}`) }}</p>

      <label class="dep-label">{{ $t('dash.overview.dashboards.default_period') }}</label>
      <select class="dep-input" :value="draft.period && draft.period.preset ? draft.period.preset : 'custom'" @change="setPeriod($event.target.value)">
        <option v-for="p in presets" :key="p.key" :value="p.key">{{ $t(p.label) }}</option>
        <option v-if="draft.period && !draft.period.preset" value="custom">{{ $t('dash.overview.dashboards.custom_period') }}</option>
      </select>

      <label class="dep-label">
        <Tooltip :text="$t('dash.overview.dashboards.pages_help')" position="top">
          <span class="dep-label-help">{{ $t('dash.overview.dashboards.pages') }}<i class="material-icons">info_outline</i></span>
        </Tooltip>
        <span class="dep-count">{{ selectedPages.length ? selectedPages.length : $t('dash.overview.dashboards.all') }}</span>
      </label>
      <PagePicker :pages="pages" :value="selectedPages" @input="ids => set('pages', ids)" />
    </div>

    <!-- Options du widget sélectionné -->
    <div v-else-if="tab === 'widget'" class="dep-body">
      <template v-if="selected">
        <label class="dep-label">{{ $t('dash.overview.dashboards.widget_title') }}</label>
        <div class="dep-title-row">
          <IconPicker
            v-if="!selectedDef.ownCard && !options.hideTitle"
            :value="options.titleIcon || null"
            :default-icon="selectedDef.icon"
            @input="v => setOption('titleIcon', v)"
          />
          <input class="dep-input" :value="options.title || ''" maxlength="60" :disabled="!!options.hideTitle" :placeholder="$t('dash.overview.dashboards.widget_title_placeholder')" @input="setOption('title', $event.target.value)" />
        </div>
        <label v-if="!selectedDef.ownCard" class="dep-check-row" @click.prevent="setOption('hideTitle', !options.hideTitle)">
          <span class="dep-check" :class="{ checked: options.hideTitle }"><i v-if="options.hideTitle" class="material-icons">check</i></span>
          {{ $t('dash.overview.dashboards.hide_title') }}
        </label>

        <!-- Panneaux du Reporting : une ou plusieurs pages (combinées par réseau) -->
        <template v-if="selectedDef.scope === 'single' && !selectedDef.singlePage">
          <label class="dep-label">
            {{ $t('dash.overview.dashboards.pages') }}
            <span class="dep-count">{{ selectedPageIds.length }}</span>
          </label>
          <p class="dep-help dep-help--tight">{{ $t('dash.overview.dashboards.panel_pages_help') }}</p>
          <PagePicker :pages="eligiblePages" :value="selectedPageIds" @input="setPages" />
        </template>

        <!-- Une seule page (concurrents) -->
        <template v-if="selectedDef.scope === 'single' && selectedDef.singlePage">
          <label class="dep-label">{{ $t('dash.overview.dashboards.page') }}</label>
          <div v-if="eligibleNetworks.length > 1" class="dep-networks">
            <button v-for="n in eligibleNetworks" :key="n" class="ov-dep-net" :class="{ active: singleNetwork === n }" @click="singleNetwork = singleNetwork === n ? null : n">
              {{ $t(`dash.overview.network.${n}`) }}
            </button>
          </div>
          <!-- Pages rangées par groupes du client ; le réseau reste indiqué -->
          <select class="dep-input" :value="selectedPageIds[0] || ''" @change="setPages($event.target.value ? [$event.target.value] : [])">
            <option value="">—</option>
            <template v-for="group in singlePageGroups">
              <optgroup v-if="group.name" :key="group.key" :label="group.name">
                <option v-for="page in group.pages" :key="`${group.key}-${page.id}`" :value="String(page.id)">{{ page.name }} ({{ $t(`dash.overview.network.${page.network}`) }})</option>
              </optgroup>
              <template v-else>
                <option v-for="page in group.pages" :key="`${group.key}-${page.id}`" :value="String(page.id)">{{ page.name }} ({{ $t(`dash.overview.network.${page.network}`) }})</option>
              </template>
            </template>
            <!-- Page choisie masquée par le filtre réseau : gardée visible dans la liste -->
            <option v-if="selectedSinglePage && !singlePageVisible" :value="String(selectedSinglePage.id)">{{ selectedSinglePage.name }} ({{ $t(`dash.overview.network.${selectedSinglePage.network}`) }})</option>
          </select>
        </template>

        <!-- Tuiles KPI retenues -->
        <template v-if="selected.module === 'kpis'">
          <label class="dep-label">{{ $t('dash.overview.dashboards.tiles') }}</label>
          <div class="dep-checks">
            <label v-for="t in kpiTiles" :key="t" class="dep-check-row" @click.prevent="toggleTile(t)">
              <span class="dep-check" :class="{ checked: tileChecked(t) }"><i v-if="tileChecked(t)" class="material-icons">check</i></span>
              {{ $t(`dash.overview.dashboards.tile.${t}`) }}
            </label>
          </div>
        </template>

        <!-- Calendrier : métrique -->
        <template v-if="selected.module === 'calendar'">
          <label class="dep-label">{{ $t('dash.overview.dashboards.metric') }}</label>
          <select class="dep-input" :value="options.metric || 'engagement'" @change="setOption('metric', $event.target.value)">
            <option value="engagement">{{ $t('dash.overview.kpi.engagement') }}</option>
            <option value="impressions">{{ $t('dash.reporting.impression') }}</option>
          </select>
        </template>

        <!-- Note libre -->
        <template v-if="selected.module === 'note'">
          <label class="dep-label">{{ $t('dash.overview.dashboards.note_text') }}</label>
          <!-- Barre de mise en forme : insère la syntaxe autour de la sélection (ou en début de ligne) -->
          <div class="dep-format">
            <button v-for="f in formats" :key="f.key" class="ov-dep-fmt" :title="$t(`dash.overview.dashboards.format.${f.key}`)" @mousedown.prevent="applyFormat(f)">
              <i v-if="f.icon" class="material-icons">{{ f.icon }}</i><span v-else :class="f.cls">{{ f.text }}</span>
            </button>
          </div>
          <textarea ref="noteText" class="dep-input dep-textarea" :value="options.text || ''" maxlength="4000" @input="setOption('text', $event.target.value)"></textarea>
          <p class="dep-help dep-help--tight">{{ $t('dash.overview.dashboards.note_help') }}</p>

          <label class="dep-label">{{ $t('dash.overview.dashboards.text_style') }}</label>
          <div class="dep-segment">
            <button v-for="a in ['left', 'center', 'right']" :key="a" class="ov-dep-seg" :class="{ active: (options.align || 'left') === a }" @click="setOption('align', a)">
              <i class="material-icons">{{ `format_align_${a}` }}</i>
            </button>
          </div>
          <div class="dep-segment dep-segment--spaced">
            <button v-for="sz in ['s', 'm', 'l']" :key="sz" class="ov-dep-seg" :class="{ active: (options.size || 'm') === sz }" @click="setOption('size', sz)">
              {{ $t(`dash.overview.dashboards.text_size.${sz}`) }}
            </button>
          </div>
          <label class="dep-label">{{ $t('dash.overview.dashboards.text_background') }}</label>
          <ColorSwatches :value="options.background || null" :presets="backgrounds" mode="background" @input="v => setOption('background', v)" />
          <label class="dep-label">{{ $t('dash.overview.dashboards.title_color') }}</label>
          <ColorSwatches :value="options.titleColor || null" :presets="textColors" mode="text" @input="v => setOption('titleColor', v)" />
          <label class="dep-label">{{ $t('dash.overview.dashboards.text_color') }}</label>
          <ColorSwatches :value="options.color || null" :presets="textColors" mode="text" @input="v => setOption('color', v)" />
        </template>

        <!-- Boutons d'action : choix et ordre (ordre de la liste), disposition -->
        <template v-if="selected.module === 'actions'">
          <label class="dep-label">{{ $t('dash.overview.dashboards.actions_label') }}</label>
          <label v-for="a in actionChoices" :key="a.key" class="dep-check-row" @click.prevent="toggleAction(a.key)">
            <span class="dep-check" :class="{ checked: selectedActions.includes(a.key) }"><i v-if="selectedActions.includes(a.key)" class="material-icons">check</i></span>
            <i class="material-icons dep-action-icon">{{ a.icon }}</i>
            <span class="dep-metric-label">{{ $t(a.label) }}</span>
          </label>
          <!-- Un seul bouton : pages auxquelles il s'applique (aucune = pages du tableau de bord) -->
          <template v-if="singlePageAction">
            <label class="dep-label">
              {{ $t('dash.overview.dashboards.action_pages') }}
              <span class="dep-count">{{ (options.actionPages || []).length || $t('dash.overview.dashboards.all') }}</span>
            </label>
            <p class="dep-help dep-help--tight">{{ $t('dash.overview.dashboards.action_pages_help') }}</p>
            <PagePicker :pages="actionPagesChoices" :value="options.actionPages || []" @input="ids => setOption('actionPages', ids)" />
          </template>
          <label class="dep-label">{{ $t('dash.overview.dashboards.actions_align') }}</label>
          <div class="dep-segment">
            <button v-for="a in ['left', 'center', 'right']" :key="a" class="ov-dep-seg" :class="{ active: (options.align || 'center') === a }" @click="setOption('align', a)">
              <i class="material-icons">{{ `format_align_${a}` }}</i>
            </button>
          </div>
          <label class="dep-label">{{ $t('dash.overview.dashboards.actions_layout') }}</label>
          <div class="dep-segment">
            <button v-for="l in ['row', 'grid']" :key="l" class="ov-dep-seg" :class="{ active: (options.layout || 'row') === l }" @click="setOption('layout', l)">
              {{ $t(`dash.overview.dashboards.actions_layout_${l}`) }}
            </button>
          </div>
        </template>

        <!-- Métrique générique -->
        <template v-if="selected.module === 'metric'">
          <label class="dep-label">{{ $t('dash.overview.dashboards.visualization') }}</label>
          <div class="dep-choices dep-choices--wrap">
            <button v-for="v in vizList" :key="v.key" class="ov-dep-choice" :class="{ active: (options.viz || 'tile') === v.key }" @click="setOption('viz', v.key)">
              <i class="material-icons">{{ v.icon }}</i>{{ $t(`dash.overview.dashboards.viz.${v.key}`) }}
            </button>
          </div>

          <label class="dep-label">{{ $t('dash.overview.dashboards.network_filter') }}</label>
          <select class="dep-input" :value="options.network || ''" @change="setOption('network', $event.target.value || null)">
            <option value="">{{ $t('dash.overview.dashboards.all_networks') }}</option>
            <option v-for="n in catalogNetworks" :key="n" :value="n">{{ $t(`dash.overview.network.${n}`) }}</option>
          </select>

          <template v-if="options.viz === 'donut' || options.viz === 'ranking'">
            <label class="dep-label">{{ $t('dash.overview.dashboards.group_by') }}</label>
            <div class="dep-segment">
              <button v-for="g in ['page', 'group', 'network']" :key="g" class="ov-dep-seg" :class="{ active: (options.groupBy || 'page') === g }" @click="setOption('groupBy', g)">
                {{ $t(`dash.overview.dashboards.by_${g}`) }}
              </button>
            </div>
          </template>
          <template v-if="options.viz === 'ranking'">
            <label class="dep-label">{{ $t('dash.overview.dashboards.top') }}</label>
            <select class="dep-input" :value="options.top || 5" @change="setOption('top', Number($event.target.value))">
              <option v-for="n in [3, 5, 10, 20]" :key="n" :value="n">{{ n }}</option>
            </select>
          </template>

          <label class="dep-check-row" @click.prevent="setOption('compare', options.compare === false)">
            <span class="dep-check" :class="{ checked: options.compare !== false }"><i v-if="options.compare !== false" class="material-icons">check</i></span>
            {{ $t('dash.overview.dashboards.compare') }}
          </label>

          <label class="dep-label">
            {{ $t('dash.overview.dashboards.metrics') }}
            <span class="dep-count">{{ selectedKeys.length }}/{{ maxKeys }}</span>
          </label>
          <p class="dep-help">{{ maxKeys > 1 ? $t('dash.overview.dashboards.metrics_help', { max: maxKeys }) : $t('dash.overview.dashboards.metrics_help_single') }}</p>
          <div class="dep-search">
            <i class="material-icons">search</i>
            <input v-model="metricSearch" :placeholder="$t('dash.overview.dashboards.search_metric')" />
          </div>
          <p v-if="catalogLoading && !metricCatalog" class="dep-help">{{ $t('dash.overview.updating') }}</p>
          <div v-for="group in metricGroups" :key="group.key" class="dep-metric-group">
            <p class="dep-cat-title">{{ group.label }}</p>
            <label v-for="m in group.metrics" :key="`${group.key}-${m.key}`" class="dep-check-row" @click.prevent="toggleMetric(m.key)">
              <span class="dep-check" :class="{ checked: selectedKeys.includes(m.key) }"><i v-if="selectedKeys.includes(m.key)" class="material-icons">check</i></span>
              <span class="dep-metric-label">{{ m.label }}</span>
              <Tooltip v-if="m.help" :text="m.help" position="left"><i class="material-icons dep-metric-help">info_outline</i></Tooltip>
            </label>
          </div>
        </template>

      </template>
    </div>
  </aside>
</template>

<script>
import Tooltip from '../../tooltip/tooltipInit.vue'
import { isReportingEligible } from '@/utils/reportingEligibility'
import { PRESETS } from '../periods'
import { CATEGORIES, WIDGETS, WIDGETS_BY_KEY } from './registry'
import { apiGet, cachedGet, rangeParams } from './widgets/widgetFetch'
import { metricLabel, metricHelp } from './widgets/metricLabels'
import { groupPages } from './pageGroups'
import PagePicker from './PagePicker.vue'
import ColorSwatches from './ColorSwatches.vue'
import IconPicker from './IconPicker.vue'
import { DASHBOARD_ACTIONS, PAGE_ACTIONS } from './dashboardActions'
import { NOTE_BACKGROUNDS, NOTE_TEXT_COLORS } from './widgets/noteColors'

// Mise en forme du widget Texte : wrap = autour de la sélection, prefix = en début de chaque ligne
const FORMATS = [
  { key: 'bold', text: 'B', cls: 'dep-fmt-b', wrap: '**' },
  { key: 'italic', text: 'I', cls: 'dep-fmt-i', wrap: '*' },
  { key: 'underline', text: 'U', cls: 'dep-fmt-u', wrap: '__' },
  { key: 'strike', text: 'S', cls: 'dep-fmt-s', wrap: '~~' },
  { key: 'highlight', icon: 'border_color', wrap: '==' },
  { key: 'title', text: 'H1', prefix: '# ' },
  { key: 'subtitle', text: 'H2', prefix: '## ' },
  { key: 'list', icon: 'format_list_bulleted', prefix: '- ' },
  { key: 'numbered', icon: 'format_list_numbered', prefix: '1. ' },
  { key: 'separator', icon: 'remove', insert: '\n---\n' }
]

const KPI_TILES = ['posted', 'success', 'fans', 'impressions', 'reach', 'engagement', 'rate']
const VIZ = [
  { key: 'tile', icon: 'looks_one' },
  { key: 'line', icon: 'show_chart' },
  { key: 'bar', icon: 'equalizer' },
  { key: 'donut', icon: 'donut_large' },
  { key: 'ranking', icon: 'format_list_numbered' }
]

/**
 * Panneau latéral de l'éditeur : catalogue de widgets, réglages du tableau de bord, options du widget sélectionné
 */
export default {
  name: 'DashboardEditorPanel',

  components: { Tooltip, PagePicker, ColorSwatches, IconPicker },

  props: {
    tab: { type: String, default: 'widgets' },
    draft: { type: Object, required: true },
    pages: { type: Array, default: () => [] },
    selected: { type: Object, default: null },
    allowed: { type: Object, default: () => ({}) },
    range: { type: Object, required: true },
    scopePageIds: { type: Array, default: null }
  },

  data() {
    return {
      tabs: ['widgets', 'settings'],
      formats: FORMATS,
      backgrounds: NOTE_BACKGROUNDS,
      textColors: NOTE_TEXT_COLORS,
      presets: PRESETS,
      kpiTiles: KPI_TILES,
      vizList: VIZ,
      search: '',
      // Filtre réseau du choix de page (widgets « une page »)
      singleNetwork: null,
      metricSearch: '',
      metricCatalog: null,
      catalogLoading: false
    }
  },

  computed: {
    catalog() {
      const q = this.search.trim().toLowerCase()
      return CATEGORIES.map(key => ({
        key,
        widgets: WIDGETS.filter(w => w.category === key && this.isAllowed(w) && this.isRelevant(w)).filter(w => !q ||
          this.$t(`dash.overview.widgets.${w.key}.title`).toLowerCase().includes(q) ||
          this.$t(`dash.overview.widgets.${w.key}.description`).toLowerCase().includes(q))
      })).filter(cat => cat.widgets.length)
    },

    // Nombre d'occurrences de chaque widget dans le tableau de bord en cours d'édition
    usedCount() {
      return (this.draft.layout || []).reduce((map, item) => ({ ...map, [item.module]: (map[item.module] || 0) + 1 }), {})
    },

    selectedPages() {
      return (this.draft.pages || []).map(String)
    },

    selectedDef() {
      return (this.selected && WIDGETS_BY_KEY[this.selected.module]) || {}
    },

    options() {
      return (this.selected && this.selected.options) || {}
    },

    scopedPages() {
      if (!this.scopePageIds) return this.pages
      return this.pages.filter(p => this.scopePageIds.includes(String(p.id)))
    },

    eligiblePages() {
      const networks = this.selectedDef.networks
      return this.scopedPages.filter(p => isReportingEligible(p) && (!networks || networks.includes(p.network)))
    },

    actionChoices() {
      const allowed = { inbox: this.allowed.inbox, reporting: this.allowed.reporting, presence: this.allowed.presence }
      return DASHBOARD_ACTIONS.filter(a => allowed[a.key] !== false)
    },

    // Un seul bouton sélectionné, et qui s'applique à des pages (création, planner, Inbox, Reporting)
    singlePageAction() {
      return this.selectedActions.length === 1 && PAGE_ACTIONS.includes(this.selectedActions[0])
    },

    // Reporting : seulement les pages éligibles ; autres boutons : toutes les pages du tableau de bord
    actionPagesChoices() {
      return this.selectedActions[0] === 'reporting' ? this.scopedPages.filter(isReportingEligible) : this.scopedPages
    },

    selectedActions() {
      return Array.isArray(this.options.actions) ? this.options.actions : []
    },

    // Le widget a sa propre sélection de pages (tableau, même vide)
    ownPages() {
      return Array.isArray(this.options.pages)
    },

    eligibleNetworks() {
      return [...new Set(this.eligiblePages.map(p => p.network))]
    },

    singlePageGroups() {
      const pages = this.eligiblePages.filter(p => !this.singleNetwork || p.network === this.singleNetwork)
      return groupPages(this.$store, pages, this.$t('dash.overview.groups.others'))
    },

    // Pages d'un panneau Reporting (pageIds ; pageId des tableaux de bord enregistrés avant le multi-pages)
    selectedPageIds() {
      if (Array.isArray(this.options.pageIds)) return this.options.pageIds.map(String)
      return this.options.pageId ? [String(this.options.pageId)] : []
    },

    selectedSinglePage() {
      return this.selectedPageIds.length ? this.eligiblePages.find(p => String(p.id) === this.selectedPageIds[0]) || null : null
    },

    singlePageVisible() {
      return !this.selectedSinglePage || this.singlePageGroups.some(g => g.pages.includes(this.selectedSinglePage))
    },

    maxKeys() {
      return ['donut', 'ranking'].includes(this.options.viz) ? 1 : 4
    },

    selectedKeys() {
      return this.options.keys && this.options.keys.length ? this.options.keys : ['impressions']
    },

    widgetPages() {
      const own = this.options.pages && this.options.pages.length ? this.options.pages.map(String) : null
      return own ? this.scopedPages.filter(p => own.includes(String(p.id))) : this.scopedPages
    },

    widgetNetworks() {
      return new Set(this.widgetPages.map(p => p.network))
    },

    // Pages envoyées au catalogue de métriques (null = toutes les pages accessibles)
    catalogPageIds() {
      const own = this.options.pages && this.options.pages.length
      return own ? this.widgetPages.map(p => String(p.id)) : this.scopePageIds
    },

    catalogScopeKey() {
      return this.selected && this.selected.module === 'metric' ? (this.catalogPageIds || ['*']).join(',') : null
    },

    catalogNetworks() {
      const list = Object.keys((this.metricCatalog && this.metricCatalog.networks) || {}).filter(n => this.widgetNetworks.has(n))
      // Filtre déjà choisi mais plus couvert par les pages : gardé pour pouvoir le retirer
      if (this.options.network && !list.includes(this.options.network)) list.push(this.options.network)
      return list
    },

    // Métriques communes puis par réseau (filtre réseau du widget appliqué), recherche sur le libellé
    metricGroups() {
      const catalog = this.metricCatalog
      if (!catalog) return []
      const q = this.metricSearch.trim().toLowerCase()
      const decorate = (list, network) => list
        .map(m => ({ key: m.key, label: metricLabel(this, m.key, network), help: metricHelp(this, m.key, network) }))
        .filter(m => !q || m.label.toLowerCase().includes(q) || m.key.toLowerCase().includes(q))
      const groups = [{ key: 'common', label: this.$t('dash.overview.dashboards.common_metrics'), metrics: decorate(catalog.common || [], this.options.network) }]
      Object.entries(catalog.networks || {})
        .filter(([network]) => this.widgetNetworks.has(network) && (!this.options.network || network === this.options.network))
        .forEach(([network, list]) => groups.push({ key: network, label: this.$t(`dash.overview.network.${network}`), metrics: decorate(list, network) }))
      return groups.filter(g => g.metrics.length)
    }
  },

  watch: {
    catalogScopeKey(value, old) {
      if (!value || value === old) return
      this.metricCatalog = null
      this.loadCatalog()
    }
  },

  created() {
    if (this.selected && this.selected.module === 'metric') this.loadCatalog()
  },

  methods: {
    // Widgets du Reporting : au moins une page éligible du tableau de bord sur un réseau concerné
    isRelevant(w) {
      if (!w.reporting || w.scope === 'none' || !this.scopedPages.length) return true
      return this.scopedPages.some(p => isReportingEligible(p) && (!w.networks || w.networks.includes(p.network)))
    },

    isAllowed(w) {
      if (w.reporting && !this.allowed.reporting) return false
      if (w.inbox && !this.allowed.inbox) return false
      if (w.presence && !this.allowed.presence) return false
      return true
    },

    tileHelp(w) {
      const description = this.$t(`dash.overview.widgets.${w.key}.description`)
      const count = this.usedCount[w.key]
      return count ? `${description} — ${this.$tc('dash.overview.dashboards.widget_used', count, { count })}` : description
    },

    onDragStart(event, w) {
      if (event.dataTransfer) {
        event.dataTransfer.setData('text/plain', w.key)
        event.dataTransfer.effectAllowed = 'copy'
      }
      this.$emit('catalog-drag', w.key)
    },

    set(field, value) {
      this.$emit('update', { field, value })
    },

    restoreName(event) {
      if (!event.target.value.trim()) event.target.value = this.draft.name
    },

    setPeriod(key) {
      if (key === 'custom') return
      this.set('period', { preset: key })
    },

    toggleAction(key) {
      const current = this.selectedActions
      // Ordre conservé : celui de la liste proposée
      const next = current.includes(key) ? current.filter(k => k !== key) : DASHBOARD_ACTIONS.map(a => a.key).filter(k => k === key || current.includes(k))
      this.setOption('actions', next)
    },

    // Insère la mise en forme autour de la sélection du texte (ou au début des lignes sélectionnées)
    applyFormat(format) {
      const area = this.$refs.noteText
      const text = this.options.text || ''
      const start = area ? area.selectionStart : text.length
      const end = area ? area.selectionEnd : text.length
      let next
      let caret
      if (format.wrap) {
        const selected = text.slice(start, end) || this.$t(`dash.overview.dashboards.format.${format.key}`)
        next = text.slice(0, start) + format.wrap + selected + format.wrap + text.slice(end)
        caret = [start + format.wrap.length, start + format.wrap.length + selected.length]
      } else if (format.prefix) {
        const lineStart = text.lastIndexOf('\n', start - 1) + 1
        const block = text.slice(lineStart, end).split('\n').map(l => format.prefix + l.replace(/^(#{1,2}\s+|-\s+|\d+[.)]\s+)/, '')).join('\n')
        next = text.slice(0, lineStart) + block + text.slice(end)
        caret = [lineStart + block.length, lineStart + block.length]
      } else {
        next = text.slice(0, start) + format.insert + text.slice(end)
        caret = [start + format.insert.length, start + format.insert.length]
      }
      this.setOption('text', next.slice(0, 4000))
      this.$nextTick(() => {
        if (!this.$refs.noteText) return
        this.$refs.noteText.focus()
        this.$refs.noteText.setSelectionRange(caret[0], caret[1])
      })
    },

    setPages(ids) {
      const options = { ...this.options, pageIds: ids.map(String) }
      delete options.pageId
      this.$emit('update-item', { i: this.selected.i, options })
    },

    setOption(key, value) {
      const options = { ...this.options, [key]: value }
      // Une seule métrique pour les répartitions et classements
      if (key === 'viz' && ['donut', 'ranking'].includes(value)) options.keys = this.selectedKeys.slice(0, 1)
      this.$emit('update-item', { i: this.selected.i, options })
    },

    toggleMetric(key) {
      let keys = this.selectedKeys.slice()
      if (keys.includes(key)) {
        if (keys.length === 1) return
        keys = keys.filter(k => k !== key)
      } else if (this.maxKeys === 1) {
        keys = [key]
      } else {
        if (keys.length >= this.maxKeys) keys.shift()
        keys.push(key)
      }
      this.setOption('keys', keys)
    },

    tileChecked(tile) {
      return !this.options.tiles || this.options.tiles.includes(tile)
    },

    toggleTile(tile) {
      const current = this.options.tiles || KPI_TILES
      const next = current.includes(tile) ? current.filter(t => t !== tile) : [...current, tile]
      if (!next.length) return
      this.setOption('tiles', next.length === KPI_TILES.length ? null : KPI_TILES.filter(t => next.includes(t)))
    },

    async loadCatalog() {
      const path = `/reporting/metrics/catalog?${rangeParams(this.range, { pages: this.catalogPageIds })}`
      const cached = cachedGet(path)
      if (cached) this.metricCatalog = cached
      this.catalogLoading = true
      try {
        this.metricCatalog = await apiGet(path)
      } catch (e) {
        if (!this.metricCatalog) this.metricCatalog = { common: [], networks: {} }
      } finally {
        this.catalogLoading = false
      }
    }
  }
}
</script>

<style scoped>
.dep {
  position: sticky;
  top: 80px;
  flex: 0 0 300px;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 160px);
  background: #fff;
  border: 1px solid #f1edf5;
  border-radius: 14px;
  box-shadow: 0 6px 24px rgba(18, 24, 38, 0.06);
  overflow: hidden;
}
/* Onglets en segment */
.dep-tabs {
  display: flex;
  gap: 2px;
  margin: 12px 12px 0;
  padding: 3px;
  border-radius: 10px;
  background: #f6f1fa;
}
.section-overview.ov.ov button.ov-dep-tab {
  flex: 1;
  padding: 6px;
  border-radius: 8px;
  color: #555e75;
  font-size: 12.5px !important;
  font-weight: 600 !important;
  transition: background 0.15s, color 0.15s;
}
.section-overview.ov.ov button.ov-dep-tab.active {
  background: #fff;
  color: #6b1f4d;
  box-shadow: 0 1px 4px rgba(18, 24, 38, 0.08) !important;
}
.dep-subhead {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #f1edf5;
}
.section-overview.ov.ov button.ov-dep-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px !important;
  border-radius: 8px;
  color: #555e75;
}
.section-overview.ov.ov button.ov-dep-back:hover {
  background: #f6f1fa;
  color: #6b1f4d;
}
.ov-dep-back .material-icons {
  font-size: 18px;
}
.dep-subhead-icon {
  font-size: 18px;
  color: #6b1f4d;
}
.dep-subhead-title {
  overflow: hidden;
  font-size: 13.5px;
  font-weight: 700;
  color: #121826;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dep-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 14px;
}
.dep-help--tight {
  margin-top: 6px !important;
}
.dep-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 4px 0 0;
  font-size: 11.5px;
  color: #9ba3b7;
}
.dep-hint .material-icons {
  font-size: 15px;
}
.dep-help {
  margin: 0 0 10px;
  font-size: 12px;
  color: #9ba3b7;
  line-height: 1.4;
}
.dep-search {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  padding: 0 10px;
  border: 1.5px solid transparent;
  border-radius: 8px;
  background: #f6f1fa;
  transition: border-color 0.15s, background 0.15s;
}
.dep-search:focus-within {
  border-color: #e0d0ea;
  background: #fff;
}
.dep-search .material-icons {
  font-size: 17px;
  color: #9ba3b7;
}
.dep-search input {
  flex: 1;
  min-width: 0;
  height: 32px;
  border: none;
  outline: none;
  font-size: 13px;
  background: transparent;
}
.dep-cat {
  margin-bottom: 12px;
}
.dep-cat-title {
  margin: 0 0 6px;
  font-size: 10.5px;
  font-weight: 600;
  color: #9ba3b7;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.dep-tiles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
}
.dep-tile-wrap {
  display: block;
  min-width: 0;
}
.dep-tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  height: 100%;
  padding: 10px;
  border: 1px solid #f1edf5;
  border-radius: 10px;
  background: #fff;
  cursor: grab;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}
.dep-tile:hover {
  border-color: #e0d0ea;
  box-shadow: 0 4px 14px rgba(107, 31, 77, 0.08);
  transform: translateY(-1px);
}
.dep-tile {
  position: relative;
}
.dep-tile--used {
  border-color: #e0d0ea;
  background: #faf6fc;
}
.dep-tile-used {
  position: absolute;
  top: 6px;
  right: 6px;
  display: inline-flex;
  align-items: center;
  gap: 1px;
  height: 18px;
  min-width: 18px;
  padding: 0 5px 0 3px;
  border-radius: 9px;
  background: #6b1f4d;
  color: #fff;
  font-size: 10.5px;
  font-style: normal;
  font-weight: 700;
}
.dep-tile .dep-tile-used .material-icons {
  font-size: 12px;
  color: #fff;
}
.dep-tile:active {
  cursor: grabbing;
}
.dep-tile .material-icons {
  font-size: 18px;
  color: #b64985;
}
.dep-tile span {
  font-size: 12px;
  font-weight: 600;
  color: #121826;
  line-height: 1.3;
}
.dep-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 14px 0 6px;
  font-size: 12px;
  font-weight: 600;
  color: #555e75;
}
.dep-label:first-of-type {
  margin-top: 0;
}
.dep-label-help {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.dep-label-help .material-icons {
  font-size: 13px;
  color: #9ba3b7;
}
.dep-count {
  padding: 0 7px;
  border-radius: 20px;
  background: #f1edf5;
  color: #6b1f4d;
  font-size: 11px;
}
.dep-input {
  width: 100%;
  height: 34px;
  padding: 0 10px;
  border: 1.5px solid #ebe3f0;
  border-radius: 8px;
  font-size: 13px;
  color: #121826;
  background: #fff;
}
.dep-input:focus {
  outline: none;
  border-color: #6b1f4d;
}
.dep-format {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  margin-bottom: 6px;
  padding: 3px;
  border-radius: 8px;
  background: #f6f1fa;
}
.section-overview.ov.ov button.ov-dep-fmt {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 26px !important;
  padding: 0 6px;
  border-radius: 6px;
  color: #555e75;
  font-size: 12.5px !important;
  font-weight: 700 !important;
}
.section-overview.ov.ov button.ov-dep-fmt:hover {
  background: #fff;
  color: #6b1f4d;
}
.ov-dep-fmt .material-icons { font-size: 16px; }
.dep-fmt-i { font-style: italic; }
.dep-fmt-u { text-decoration: underline; }
.dep-fmt-s { text-decoration: line-through; }
.dep-segment--spaced { margin-top: 6px; }
.dep-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.dep-title-row {
  display: flex;
  gap: 6px;
}
.dep-title-row .dep-input {
  flex: 1;
  min-width: 0;
}
.dep-action-icon { font-size: 16px; color: #b64985; }
.dep-textarea {
  height: 140px;
  padding: 8px 10px;
  resize: vertical;
}
.dep-choices {
  display: flex;
  gap: 6px;
}
.dep-choices--wrap {
  flex-wrap: wrap;
}
.dep-choice-wrap {
  flex: 1;
}
.section-overview.ov.ov button.ov-dep-choice {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  padding: 7px 10px;
  border: 1.5px solid #e0d0ea;
  border-radius: 8px;
  color: #555e75;
  font-size: 12.5px !important;
  font-weight: 500 !important;
}
.dep-choices--wrap .ov-dep-choice {
  width: auto !important;
}
.section-overview.ov.ov button.ov-dep-choice.active {
  border-color: #6b1f4d;
  background: #f1edf5;
  color: #6b1f4d;
  font-weight: 600 !important;
}
.ov-dep-choice .material-icons {
  font-size: 16px;
}
.dep-networks {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 8px;
}
.section-overview.ov.ov button.ov-dep-net {
  padding: 3px 9px;
  border: 1px solid #e0d0ea;
  border-radius: 20px;
  color: #6b1f4d;
  font-size: 11.5px !important;
}
.section-overview.ov.ov button.ov-dep-net.active {
  background: #6b1f4d;
  color: #fff;
}
.dep-segment {
  display: flex;
  gap: 2px;
  padding: 3px;
  border-radius: 10px;
  background: #f6f1fa;
}
.section-overview.ov.ov button.ov-dep-seg {
  display: inline-flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 6px;
  border-radius: 8px;
  color: #555e75;
  font-size: 12.5px !important;
  font-weight: 600 !important;
}
.section-overview.ov.ov button.ov-dep-seg.active {
  background: #fff;
  color: #6b1f4d;
  box-shadow: 0 1px 4px rgba(18, 24, 38, 0.08) !important;
}
.ov-dep-seg .material-icons {
  font-size: 15px;
}
.dep-check-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  border-radius: 6px;
  font-size: 12.5px;
  color: #121826;
  cursor: pointer;
}
.dep-check-row:hover {
  background: #f6f1fa;
}
.dep-metric-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dep-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  border: 1.5px solid #c9b8d4;
  border-radius: 4px;
  background: #fff;
}
.dep-check.partial {
  border-color: #6b1f4d;
  background: #fff;
}
.dep-check.partial .material-icons {
  color: #6b1f4d;
}
.dep-check.checked {
  border-color: #6b1f4d;
  background: #6b1f4d;
}
.dep-check .material-icons {
  font-size: 13px;
  color: #fff;
}
.dep-metric-group {
  margin-bottom: 10px;
}
.dep-metric-help {
  font-size: 14px;
  color: #9ba3b7;
}
@media (max-width: 1100px) {
  .dep {
    flex-basis: 280px;
  }
}
</style>
